import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { GridColumn } from 'src/components/GridWrapper';
import { CanUsesInitialBudgets } from 'src/components/guards/PermissionsList';
import usePermission from 'src/hooks/usePermissions';
import DateCell from 'src/sections/view_checkbook/hold_amount_cell/DateCell';
import Action from 'src/sections/view_checkbook/spent_amount_cell/Action';
import GoToPoJournal from 'src/sections/view_checkbook/spent_amount_cell/GoToPoJournal';
import TotalAmountCell from 'src/sections/view_checkbook/transaction_amount_cell/TotalAmountCell';

export const useTransactionColumns: (isAllLog: boolean) => GridColumn<any>[] = (isAllLog) => {
  const { t } = useTranslation();
  const hasUsesInitialBudgetsPermission = usePermission({
    permissions: CanUsesInitialBudgets,
    companyAttribute: true,
  });

  const transactionColumns: GridColumn<any>[] = useMemo(
    () => [
      ...(isAllLog
        ? ([
            {
              field: 'glCode',
              title: t('GL Code'),
              filter: 'text',
            },
          ] as const)
        : []),
      {
        field: 'date',
        title: t('Date'),
        filter: 'date',
        component: DateCell,
        headerCellTooltip: 'Transaction Date',
      },
      {
        field: 'user',
        title: t('Contact'),
        filter: 'text',
        dataType: 'string',
      },
      {
        field: 'documentNumber',
        title: t('PO/Journal Entry #'),
        filter: 'text',
        dataType: 'string',
        component: GoToPoJournal,
        width: 100,
      },
      {
        field: 'action',
        title: t('Action'),
        filter: 'text',
        dataType: 'string',
        component: Action,
        headerCellTooltip: 'Type of Action',
        width: 250,
      },
      {
        field: 'total',
        title: t('$ Total'),
        filter: 'numeric',
        dataType: 'dollar',
        format: '{0:c2}',
        filterFormat: 'c2',
        component: TotalAmountCell,
        headerCellTooltip: 'Transaction amount for this GL code and period',
      },
      ...(isAllLog && hasUsesInitialBudgetsPermission
        ? ([
            {
              field: 'initialBudget',
              title: t('Budget Amt'),
              filter: 'numeric',
              format: '{0:c2}',
              filterFormat: 'c',
              dataType: 'dollar',
            },
          ] as const)
        : []),
      ...(isAllLog
        ? ([
            {
              field: 'forecast',
              title: t('Forecast Amt'),
              filter: 'numeric',
              dataType: 'dollar',
              format: '{0:c2}',
              filterFormat: 'c2',
            },
            {
              field: 'balance',
              title: t('Remaining Forecast Amt'),
              filter: 'numeric',
              dataType: 'dollar',
              format: '{0:c2}',
              filterFormat: 'c2',
            },

            ...(hasUsesInitialBudgetsPermission
              ? [
                  {
                    field: 'remainingBudgetAmount',
                    title: t('Remaining Budget Amt'),
                    filter: 'numeric',
                    dataType: 'dollar',
                    format: '{0:c2}',
                    filterFormat: 'c2',
                  },
                ]
              : []),
          ] as GridColumn<any>[])
        : ([
            ...(hasUsesInitialBudgetsPermission
              ? [
                  {
                    field: 'initialBudget',
                    title: t('Budget Amt'),
                    filter: 'numeric',
                    dataType: 'dollar',
                    format: '{0:c2}',
                    filterFormat: 'c2',
                  },
                ]
              : []),
            {
              field: 'forecast',
              title: t('Forecast Amt'),
              filter: 'numeric',
              dataType: 'dollar',
              format: '{0:c2}',
              filterFormat: 'c2',
            },

            {
              field: 'balance',
              title: t('Remaining Forecast Amt'),
              filter: 'numeric',
              dataType: 'dollar',
              format: '{0:c2}',
              filterFormat: 'c2',
            },

            ...(hasUsesInitialBudgetsPermission
              ? [
                  {
                    field: 'remainingBudgetAmount',
                    title: t('Remaining Budget Amt'),
                    filter: 'numeric',
                    dataType: 'dollar',
                    format: '{0:c2}',
                    filterFormat: 'c2',
                  },
                ]
              : []),
          ] as GridColumn<any>[])),
      {
        field: 'comments',
        title: t('Comments'),
        filter: 'text',
        dataType: 'string',
      },
    ],
    [hasUsesInitialBudgetsPermission, isAllLog, t]
  );
  return transactionColumns;
};
